import {
  useCallback,
  useState,
} from "react";

import facilityService from "../services/facilityService";

const useFacilities = () => {
  const [
    facilities,
    setFacilities,
  ] = useState([]);

  const [
    statistics,
    setStatistics,
  ] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending_approval: 0,
    hospitals: 0,
    long_term_care: 0,
    clinics: 0,
  });

  const [
    selectedFacility,
    setSelectedFacility,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const loadFacilities =
    useCallback(
      async (
        params = {}
      ) => {
        try {
          setLoading(true);
          setError("");

          const data =
            await facilityService
              .getFacilities(
                params
              );

          const rows =
            Array.isArray(
              data?.facilities
            )
              ? data.facilities
              : [];

          setFacilities(
            rows
          );

          setStatistics(
            data?.statistics || {
              total: 0,
              active: 0,
              inactive: 0,
              pending_approval: 0,
              hospitals: 0,
              long_term_care: 0,
              clinics: 0,
            }
          );

          return data;
        } catch (loadError) {
          setError(
            loadError.message ||
              "Unable to load facilities."
          );

          throw loadError;
        } finally {
          setLoading(false);
        }
      },
      []
    );

  const loadFacilityById =
    useCallback(
      async (
        facilityId
      ) => {
        try {
          setLoading(true);
          setError("");

          const data =
            await facilityService
              .getFacilityById(
                facilityId
              );

          setSelectedFacility(
            data?.facility ||
              null
          );

          return (
            data?.facility ||
            null
          );
        } catch (loadError) {
          setError(
            loadError.message ||
              "Unable to load facility."
          );

          throw loadError;
        } finally {
          setLoading(false);
        }
      },
      []
    );

  const createFacility =
    useCallback(
      async (
        payload
      ) => {
        try {
          setLoading(true);
          setError("");

          return await facilityService
            .createFacility(
              payload
            );
        } catch (saveError) {
          setError(
            saveError.message ||
              "Unable to create facility."
          );

          throw saveError;
        } finally {
          setLoading(false);
        }
      },
      []
    );

  const updateFacility =
    useCallback(
      async (
        facilityId,
        payload
      ) => {
        try {
          setLoading(true);
          setError("");

          return await facilityService
            .updateFacility(
              facilityId,
              payload
            );
        } catch (saveError) {
          setError(
            saveError.message ||
              "Unable to update facility."
          );

          throw saveError;
        } finally {
          setLoading(false);
        }
      },
      []
    );

  const setFacilityStatus =
    useCallback(
      async (
        facilityId,
        isActive
      ) => {
        try {
          setLoading(true);
          setError("");

          return await facilityService
            .setFacilityStatus(
              facilityId,
              isActive
            );
        } catch (statusError) {
          setError(
            statusError.message ||
              "Unable to update facility status."
          );

          throw statusError;
        } finally {
          setLoading(false);
        }
      },
      []
    );

  const clearError = () => {
    setError("");
  };

  return {
    facilities,
    statistics,
    selectedFacility,
    loading,
    error,

    setSelectedFacility,
    clearError,

    loadFacilities,
    loadFacilityById,
    createFacility,
    updateFacility,
    setFacilityStatus,
  };
};

export default useFacilities;